"use client";
import { Button, FormError, FormGroup, Input, Label } from "cy-fe";
import { Controller, useForm } from "react-hook-form";
import * as StepWizard from "cy-ui/components/StepWizard";
import { BodyContainer } from "cy-remittance/components/layouts";
import { PersonalInfoInputs } from "./models";
import { GenderComboBox } from "cy-remittance/registration/GenderComboBox";
import { HeaderText } from "cy-remittance/components/HeaderText";
import { DateOfBirth } from "./DateOfBirth";
import {
  FundSource,
  Nationality,
  Occupation,
} from "cy-api/simple-payments/ref-data/models";
import { SelectComboBox } from "../components/SelectComboBox";
import { ComponentType } from "react";

export interface NationalIdProps {
  form: ReturnType<typeof useForm<PersonalInfoInputs>>;
}
export function PersonalInfoPage({
  initial,
  onSubmit,
  backUrl,
  occupations,
  nationalities,
  sourceOfFunds,
  NationalId,
}: {
  onSubmit: (args: PersonalInfoInputs) => void;
  initial: Partial<PersonalInfoInputs>;
  hasAccountLimit: boolean;
  backUrl: string;
  occupations: Occupation[];
  sourceOfFunds: FundSource[];
  nationalities: Nationality[];
  NationalId?: ComponentType<NationalIdProps>;
}) {
  const form = useForm<PersonalInfoInputs>({ defaultValues: initial });
  function handleSubmit(values: PersonalInfoInputs) {
    onSubmit(values);
  }

  return (
    <StepWizard.StepPageLayout
      backUrl={backUrl}
      headerRender={() => <HeaderText vCenter>Personal information</HeaderText>}
      subHeaderRender={() => <>Tell us about yourself</>}
    >
      <form
        data-testid="personal-info-form"
        className="w-full space-y-5"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <BodyContainer className="mt-10">
          <FormGroup controller={{ control: form.control, name: "firstName" }}>
            <Label>First name</Label>
            <Input
              tabIndex={0}
              vSize="lg"
              data-testid="first-name-input"
              {...form.register("firstName", {
                required: { value: true, message: "required" },
              })}
            />
            <FormError />
          </FormGroup>
          <FormGroup controller={{ control: form.control, name: "middleName" }}>
            <Label>Middle name</Label>
            <Input
              vSize="lg"
              data-testid="middle-name-input"
              {...form.register("middleName")}
            />
            <FormError />
          </FormGroup>
          <FormGroup controller={{ control: form.control, name: "lastName" }}>
            <Label>Last name</Label>
            <Input
              vSize="lg"
              data-testid="last-name-input"
              {...form.register("lastName", {
                required: { value: true, message: "required" },
              })}
            />
            <FormError />
          </FormGroup>
          <FormGroup controller={{ control: form.control, name: "email" }}>
            <Label>Email address (Optional)</Label>
            <Input
              type="email"
              vSize="lg"
              data-testid="email-input"
              placeholder="Enter your email address"
              {...form.register("email", {
                setValueAs: (value: string) => value?.trim() || undefined,
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Please enter a valid email address",
                },
              })}
            />
            <FormError />
          </FormGroup>
          {NationalId ? <NationalId form={form} /> : null}
          <div data-testid="gender-field">
            <Label>Gender</Label>
            <GenderComboBox form={form} />
          </div>
          <div data-testid="date-of-birth-field">
            <DateOfBirth form={form} />
          </div>
          <Controller
            control={form.control}
            name="occupation"
            rules={{ required: { value: true, message: "Required" } }}
            render={({ field, fieldState }) => {
              return (
                <div data-testid="occupation-field">
                  <Label required>Occupation</Label>
                  <SelectComboBox
                    label="occupation"
                    options={occupations}
                    selectedId={field.value || null}
                    error={!!fieldState.error}
                    onChange={(x) => field.onChange(x)}
                    data-testid="occupation-select"
                  />
                  <FormError>{fieldState.error ? "Required" : null}</FormError>
                </div>
              );
            }}
          />
          <Controller
            control={form.control}
            name="sourceofFunds"
            rules={{ required: { value: true, message: "Required" } }}
            render={({ field, fieldState }) => {
              return (
                <div data-testid="source-of-funds-field">
                  <Label required>Source of funds</Label>
                  <SelectComboBox
                    label="source of funds"
                    options={sourceOfFunds}
                    selectedId={field.value || null}
                    error={!!fieldState.error}
                    onChange={(x) => field.onChange(x)}
                    data-testid="source-of-funds-select"
                  />
                  <FormError>{fieldState.error ? "Required" : null}</FormError>
                </div>
              );
            }}
          />
          <Controller
            control={form.control}
            name="nationality"
            rules={{ required: { value: true, message: "Required" } }}
            render={({ field, fieldState }) => {
              return (
                <div data-testid="nationality-field">
                  <Label required>Nationality</Label>
                  <SelectComboBox
                    label="nationality"
                    options={nationalities}
                    selectedId={field.value || null}
                    error={!!fieldState.error}
                    onChange={(x) => field.onChange(x)}
                    data-testid="nationality-select"
                  />
                  <FormError>{fieldState.error ? "Required" : null}</FormError>
                </div>
              );
            }}
          />
          <Button
            className="!mt-10 w-full"
            loading={form.formState.isSubmitting}
            vSize="lg"
            data-testid="personal-info-next-button"
          >
            Next
          </Button>
        </BodyContainer>
      </form>
    </StepWizard.StepPageLayout>
  );
}
